import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'

import EnterpriseBillingStatus from './EnterpriseBillingStatus'
import PendingInvoiceSection from './PendingInvoiceSection'

import styles from './ManageEnterprises.module.scss'
import sharedStyles from '@/pages/app/Enterprises/components/styles.module.scss'

import { EnterpriseListItem } from '@/api/generated'
import Button from '@/components/Button'
import { setImpersonation } from '@/lib/impersonation'
import BulkMemberActions from '@/pages/app/Enterprises/components/BulkMemberActions'
import EnterpriseAccessManagement from '@/pages/app/Enterprises/components/EnterpriseAccessManagement'
import EnterpriseDetails from '@/pages/app/Enterprises/components/EnterpriseDetails'
import EnterpriseDomains from '@/pages/app/Enterprises/components/EnterpriseDomains'
import EnterpriseSearch from '@/pages/app/Enterprises/components/EnterpriseSearch'
import EnterpriseSettings from '@/pages/app/Enterprises/components/EnterpriseSettings'
import MembersList from '@/pages/app/Enterprises/components/MembersList'
import PublicApiAccess from '@/pages/app/Enterprises/components/PublicApiAccess'
import { AppDispatch } from '@/store'
import { getEnterpriseThunk } from '@/store/features/user/userSlice'
import { resetTenantState } from '@/store/resetTenantState'

const ManageEnterprises = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  // Deep-link support (e.g. the Enterprise link on the Manage User tab):
  // ?enterpriseId=...&enterpriseName=... preselects that enterprise.
  const initialId = searchParams.get('enterpriseId')
  const initialName = searchParams.get('enterpriseName')
  const [selectedEnterprise, setSelectedEnterprise] = useState<{ id: string; name: string } | null>(
    initialId && initialName ? { id: initialId, name: initialName } : null
  )

  const handleSelectEnterprise = (enterprise: EnterpriseListItem) => {
    setSelectedEnterprise({ id: enterprise.id, name: enterprise.name })
    setError(null)
  }

  const handleViewAsAdmin = () => {
    if (!selectedEnterprise) return

    setImpersonation({
      enterpriseId: selectedEnterprise.id,
      enterpriseName: selectedEnterprise.name,
    })
    // Clear cached data so pages re-fetch with impersonation header
    resetTenantState(dispatch, 'enterprise')
    // Re-fetch enterprise data with impersonation header active
    dispatch(getEnterpriseThunk({ needSubscription: false }))
      .unwrap()
      .then(() => navigate('/app/team'))
      .catch(() => {
        setImpersonation(null)
        setError('Failed to load enterprise data')
      })
  }

  return (
    <div className={styles.manageContainer}>
      {/* Enterprise Search */}
      <div className={styles.header}>
        <EnterpriseSearch onSelect={handleSelectEnterprise} onError={setError} />
      </div>

      {/* Error Display */}
      {error && (
        <div className={styles.errorBox}>
          <span className={styles.errorMessage}>{error}</span>
          <Button title="x" onClick={() => setError(null)} type="link" className={styles.errorDismiss} />
        </div>
      )}

      {/* Selected Enterprise Content */}
      {selectedEnterprise ? (
        <>
          <div className={styles.enterpriseHeader}>
            <div className={styles.enterpriseInfo}>
              <h4>{selectedEnterprise.name}</h4>
              <EnterpriseBillingStatus enterpriseId={selectedEnterprise.id} />
            </div>
            <div className={styles.headerActions}>
              <Button title="View as Admin" type="dark" onClick={handleViewAsAdmin} />
            </div>
          </div>

          <PendingInvoiceSection enterpriseId={selectedEnterprise.id} />

          <EnterpriseDetails
            key={`details-${selectedEnterprise.id}`}
            enterpriseId={selectedEnterprise.id}
            enterpriseName={selectedEnterprise.name}
            onDelete={() => setSelectedEnterprise(null)}
            onNameChange={(name) => setSelectedEnterprise((prev) => (prev ? { ...prev, name } : prev))}
          />

          <EnterpriseDomains
            key={`domains-${selectedEnterprise.id}`}
            enterpriseId={selectedEnterprise.id}
            enterpriseName={selectedEnterprise.name}
          />

          <EnterpriseAccessManagement
            key={`access-${selectedEnterprise.id}`}
            enterpriseId={selectedEnterprise.id}
            enterpriseName={selectedEnterprise.name}
          />

          <EnterpriseSettings key={`settings-${selectedEnterprise.id}`} enterpriseId={selectedEnterprise.id} />

          <PublicApiAccess
            key={`public-api-${selectedEnterprise.id}`}
            enterpriseId={selectedEnterprise.id}
            enterpriseName={selectedEnterprise.name}
          />

          <BulkMemberActions
            enterpriseId={selectedEnterprise.id}
            enterpriseName={selectedEnterprise.name}
            onError={setError}
          />

          <MembersList
            key={`members-${selectedEnterprise.id}`}
            enterpriseId={selectedEnterprise.id}
            enterpriseName={selectedEnterprise.name}
          />
        </>
      ) : (
        <div className={sharedStyles.emptyState}>Search and select an enterprise to manage its members</div>
      )}
    </div>
  )
}

export default ManageEnterprises
